/**
 * Vendor Import — Mapping review section (one entity type).
 *
 * Renders a single entity type's proposed property mappings as up to three
 * collapsible groups — Mapped to existing / Will create / Skipped — built
 * from the effective-action partition, plus a heading, optional truncation
 * notice, and a one-line count summary.
 */

import { useCallback, useState } from 'react';
import type { Key } from 'react-aria-components';

import { Accordion } from '@/lib/base/accordion';
import { Table } from '@/lib/base/table/table';

import { LockedMappingsTable } from './LockedMappingsTable';
import { MappingReviewGroup } from './MappingReviewGroup';
import { partitionProposalsByAction, reviewRowKey } from './reviewGrouping';
import { ReviewRow } from './ReviewRow';
import type { CoreMapping, MappingAction, MappingProposal } from './types';

/** Groups with `create_new` counts at or below this auto-expand on mount. */
const CREATE_AUTO_EXPAND_MAX = 10;

interface ProposalTableProps {
  entityType: string;
  entityLabel: string;
  groupTitle: string;
  proposals: MappingProposal[];
  totalRecords: number | undefined;
  actionByKey: Record<string, MappingAction>;
  onActionChange: (key: string, action: MappingAction) => void;
}

/** Editable 4-column table of proposal rows, reused by all three groups. */
function ProposalTable({
  entityType,
  entityLabel,
  groupTitle,
  proposals,
  totalRecords,
  actionByKey,
  onActionChange,
}: ProposalTableProps) {
  return (
    <div className='overflow-x-auto rounded-lg border border-[var(--color-border-secondary)]'>
      <Table
        aria-label={`${entityLabel} property mappings – ${groupTitle}`}
        size='sm'
      >
        <Table.Header>
          <Table.Head label='Field' isRowHeader />
          <Table.Head label='Mapping' />
          <Table.Head label='Records with a value' />
          <Table.Head label='Action' />
        </Table.Header>
        <Table.Body>
          {proposals.map(proposal => {
            const rowKey = reviewRowKey(entityType, proposal.raw_key);
            return (
              <ReviewRow
                key={rowKey}
                rowKey={rowKey}
                proposal={proposal}
                totalRecords={totalRecords}
                selectedAction={actionByKey[rowKey] ?? proposal.proposed_action}
                onActionChange={onActionChange}
              />
            );
          })}
        </Table.Body>
      </Table>
    </div>
  );
}

export interface MappingReviewSectionProps {
  entityType: string;
  entityLabel: string;
  proposals: MappingProposal[];
  coreMappings: CoreMapping[];
  totalRecords: number | undefined;
  isTruncated: boolean;
  actionByKey: Record<string, MappingAction>;
  onActionChange: (key: string, action: MappingAction) => void;
}

export function MappingReviewSection({
  entityType,
  entityLabel,
  proposals,
  coreMappings,
  totalRecords,
  isTruncated,
  actionByKey,
  onActionChange,
}: MappingReviewSectionProps) {
  const { mapExisting, createNew, skip } = partitionProposalsByAction(
    entityType,
    proposals,
    actionByKey
  );

  const mappedCount = mapExisting.length + coreMappings.length;
  const createCount = createNew.length;
  const skipCount = skip.length;

  const defaultExpandedKeys =
    createCount > 0 && createCount <= CREATE_AUTO_EXPAND_MAX
      ? [`${entityType}-create`]
      : [];

  // Row-level content (ProposalTable / LockedMappingsTable) is expensive to
  // mount for large groups, so it's deferred until a group has been expanded
  // at least once. A group that starts expanded via `defaultExpandedKeys`
  // must be marked opened immediately so its rows render on first paint.
  const [openedIds, setOpenedIds] = useState<Set<string>>(
    () => new Set(defaultExpandedKeys)
  );

  const handleExpandedChange = useCallback((keys: Set<Key>) => {
    setOpenedIds(prev => {
      let changed = false;
      const next = new Set(prev);
      keys.forEach(key => {
        const id = String(key);
        if (!next.has(id)) {
          next.add(id);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, []);

  return (
    <div className='flex flex-col gap-2'>
      <h3 className='text-sm font-semibold text-primary'>{entityLabel}</h3>
      {isTruncated && (
        <p className='text-sm text-[var(--color-text-warning-primary)]'>
          Showing the first {proposals.length} properties for {entityLabel}.
          Additional properties were not loaded for review.
        </p>
      )}
      <p className='text-sm text-tertiary'>
        {mappedCount} mapped · {createCount} new · {skipCount} skipped
      </p>
      <Accordion
        allowsMultipleExpanded
        defaultExpandedKeys={defaultExpandedKeys}
        onExpandedChange={handleExpandedChange}
        className='flex flex-col gap-3'
      >
        {mappedCount > 0 && (
          <MappingReviewGroup
            id={`${entityType}-mapped`}
            title='Mapped to existing'
            count={mappedCount}
          >
            {openedIds.has(`${entityType}-mapped`) && (
              <>
                {coreMappings.length > 0 && (
                  <LockedMappingsTable
                    entityType={entityType}
                    entityLabel={entityLabel}
                    mappings={coreMappings}
                  />
                )}
                {mapExisting.length > 0 && (
                  <ProposalTable
                    entityType={entityType}
                    entityLabel={entityLabel}
                    groupTitle='Mapped to existing'
                    proposals={mapExisting}
                    totalRecords={totalRecords}
                    actionByKey={actionByKey}
                    onActionChange={onActionChange}
                  />
                )}
              </>
            )}
          </MappingReviewGroup>
        )}
        {createCount > 0 && (
          <MappingReviewGroup
            id={`${entityType}-create`}
            title='Will create'
            count={createCount}
          >
            {openedIds.has(`${entityType}-create`) && (
              <ProposalTable
                entityType={entityType}
                entityLabel={entityLabel}
                groupTitle='Will create'
                proposals={createNew}
                totalRecords={totalRecords}
                actionByKey={actionByKey}
                onActionChange={onActionChange}
              />
            )}
          </MappingReviewGroup>
        )}
        {skipCount > 0 && (
          <MappingReviewGroup
            id={`${entityType}-skip`}
            title='Skipped – no values'
            count={skipCount}
          >
            {openedIds.has(`${entityType}-skip`) && (
              <ProposalTable
                entityType={entityType}
                entityLabel={entityLabel}
                groupTitle='Skipped – no values'
                proposals={skip}
                totalRecords={totalRecords}
                actionByKey={actionByKey}
                onActionChange={onActionChange}
              />
            )}
          </MappingReviewGroup>
        )}
      </Accordion>
    </div>
  );
}
